import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'

import { Text, ConfirmationResultModal } from '@percent/lemonade'
import { useMutation, APIErrorHandler, API_ERROR_MESSAGE } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import type { ReactivateAccountModalProps } from './ReactivateAccountModal.types'

export function ReactivateAccountModal({ isOpen, onClose, onSuccess, account }: ReactivateAccountModalProps) {
  const { t } = useTranslation()
  const { accountService } = useServices()
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  const [{ status, isPending }, { mutateAsync: reactivateAccount }] = useMutation(accountService.reactivateAccount)

  const state = status === 'success' ? 'success' : status === 'error' ? 'failure' : undefined

  const memberDisplay = account.fullName ? `${account.fullName} (${account.email})` : account.email

  const handleConfirm = async () => {
    setErrorMessage(undefined)
    try {
      await reactivateAccount(account)
      onSuccess?.()
    } catch (err) {
      const axiosError = err as AxiosError<{ error: Record<string, unknown> }>
      const apiError = APIErrorHandler(axiosError?.response?.data?.error)
      setErrorMessage(
        apiError === API_ERROR_MESSAGE.genericError
          ? t('account.modify.reactivate.failure.description', { member: memberDisplay })
          : apiError
      )
    }
  }

  const handleClose = () => {
    if (!isPending) {
      setErrorMessage(undefined)
      onClose()
    }
  }

  return (
    <ConfirmationResultModal
      success={{
        title: t('account.modify.reactivate.success'),
        description: t('account.modify.reactivate.success.description', { member: memberDisplay }),
        button: t('button.close')
      }}
      failure={{
        title: t('account.modify.reactivate.failure'),
        description: errorMessage ?? t('account.modify.reactivate.failure.description', { member: memberDisplay }),
        button: t('button.close')
      }}
      title={t('account.modify.reactivate.title')}
      primaryButtonText={t('account.modify.reactivate.confirmButton')}
      secondaryButtonText={t('button.cancel')}
      primaryBtnTestId="reactivate-account-submit-button"
      secondaryBtnTestId="reactivate-account-cancel-button"
      buttonTestId="reactivate-account-close-button"
      state={state}
      loading={isPending}
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
    >
      <Text>{t('account.modify.reactivate.description', { member: memberDisplay })}</Text>
    </ConfirmationResultModal>
  )
}
